import React, { useState, useEffect, useRef } from "react";
import useScrollReveal from "../hooks/useScrollReveal";
import "../../styles/module/process.css";
import processRequirementsImg from "../../assets/images/img/process-requirements.jpg";
import processPlanningImg from "../../assets/images/img/process-planning.jpg";
import processDevelopmentImg from "../../assets/images/img/process-development.jpg";
import processTestingImg from "../../assets/images/img/process-testing.jpg";
import processLaunchImg from "../../assets/images/img/process-launch.jpg";
import processMaintenanceImg from "../../assets/images/img/process-maintenance.jpg";

// Step Process Data
const processSteps = [
  {
    id: 1,
    title: "Discovery & Requirements",
    image: processRequirementsImg,
    paragraph1:
      "Every successful project begins with a clear understanding of your business goals, target audience, and digital roadmap.",
    paragraph2: (
      <>
        We take the time to analyze your brand, market challenges, and long-term vision.
        Through strategic consultation and deep discovery, we turn key business insights
        into a focused technical and creative blueprint for your solution.
      </>
    ),
  },
  {
    id: 2,
    title: "Strategy & UI/UX Design",
    image: processPlanningImg,
    paragraph1:
      "With a solid strategic foundation, we translate your ideas into intuitive, engaging, and visually stunning user experiences.",
    paragraph2: (
      <>
        We architect structure, user flows, and interactive prototypes before refining the
        visual brand identity. From wireframes and layouts to typography, colors, and
        interactions, every detail is engineered for maximum user engagement.
      </>
    ),
  },
  {
    id: 3,
    title: "Engineering & Development",
    image: processDevelopmentImg,
    paragraph1:
      "Once designs are finalized, our specialized engineers build your custom solution using modern, high-performance technology.",
    paragraph2: (
      <>
        We transform approved prototypes into robust, responsive, and scalable software platforms.
        Implementing clean code, seamless API integrations, and robust architecture, we ensure your
        platform is fast, secure, and built for growth.
      </>
    ),
  },
  {
    id: 4,
    title: "Testing & Quality Assurance",
    image: processTestingImg,
    paragraph1:
      "Before deployment, we conduct rigorous multi-platform testing to guarantee optimal performance, security, and flawlessness.",
    paragraph2: (
      <>
        We test across diverse devices, operating systems, and browsers to ensure seamless responsiveness.
        From functional validation and speed optimization to accessibility, SEO readiness, and security audits,
        we polish every element to perfection.
      </>
    ),
  },
  {
    id: 5,
    title: "Deployment & Launch",
    image: processLaunchImg,
    paragraph1:
      "When every test passes and you approve the final build, we execute a seamless, stress-free production launch.",
    paragraph2: (
      <>
        We manage server configuration, domain alignment, and live deployment with meticulous precision.
        Our team monitors the rollout in real time, ensuring a smooth transition so your digital platform
        starts driving impact immediately.
      </>
    ),
  },
  {
    id: 6,
    title: "Maintenance & Continuous Support",
    image: processMaintenanceImg,
    paragraph1:
      "Our partnership extends far beyond launch. We provide ongoing support, technical updates, and continuous optimization.",
    paragraph2: (
      <>
        From security updates and system enhancements to analytics monitoring and feature additions,
        we keep your digital ecosystem secure and peak-performing. As your business scales, we empower
        your platform to evolve right alongside it.
      </>
    ),
  },
];

export default function Process() {
  useScrollReveal("slide-from-left");
  useScrollReveal("slide-from-right");
  useScrollReveal("drop-from-top");

  const [activeStep, setActiveStep] = useState(1);
  const cardRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const stepId = Number(entry.target.getAttribute("data-step-id"));
            if (stepId) {
              setActiveStep(stepId);
            }
          }
        });
      },
      {
        threshold: 0.4,
        rootMargin: "-10% 0px -25% 0px",
      }
    );

    cardRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section className="process-section">
      <div className="process-container">
        <div className="process-grid">
          {/* Left Column: Pinned Sticky Heading, Description & Active Step Counter */}
          <div className="process-left-col">
            <h5 className="drop-from-top">Our Process</h5>
            <h2 style={{ marginBottom: 30 }}>From Idea to Impact</h2>

            <p style={{ marginBottom: 20 }}>
              Great digital experiences begin with a clear strategy. We follow a focused, transparent process that turns ideas into impactful digital solutions.
            </p>

            <p style={{ marginBottom: 0 }}>
              From strategy and design to development, we collaborate closely at every step to create seamless, engaging experiences that help your business grow.
            </p>

            {/* Active Step Indicator Capsule Pill */}
            <div className="process-step-indicator">
              <span className="process-step-current">
                {String(activeStep).padStart(2, "0")}
              </span>
              <span className="process-step-divider"> / </span>
              <span className="process-step-total">
                {String(processSteps.length).padStart(2, "0")}
              </span>
            </div>
          </div>

          {/* Right Column: Cards Stacked in Normal Document Flow */}
          <div className="process-right-col slide-from-right">
            <div className="process-cards-stack">
              {processSteps.map((step, index) => (
                <div
                  key={step.id}
                  data-step-id={step.id}
                  ref={(el) => (cardRefs.current[index] = el)}
                  className="process-card"
                >
                  <div className="process-card-image-wrap">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="process-card-image"
                      loading="lazy"
                    />

                    {/* Starburst Badge Icon with Step Number */}
                    <div className="process-step-badge">
                      <svg
                        className="process-badge-svg"
                        viewBox="0 0 44 44"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M22 0L25.9 3.8L31.2 2.2L33.7 7.1L39.1 7.9L39.8 13.3L44 16.8L42.6 22L44 27.2L39.8 30.7L39.1 36.1L33.7 36.9L31.2 41.8L25.9 40.2L22 44L18.1 40.2L12.8 41.8L10.3 36.9L4.9 36.1L4.2 30.7L0 27.2L1.4 22L0 16.8L4.2 13.3L4.9 7.9L10.3 7.1L12.8 2.2L18.1 3.8L22 0Z"
                          fill="#E63946"
                        />
                      </svg>
                      <span className="process-badge-text">{step.id}</span>
                    </div>
                  </div>

                  <div className="process-card-body">
                    <h3 className="process-card-title">{step.title}</h3>
                    <p className="process-card-text">{step.paragraph1}</p>
                    <p className="process-card-text">{step.paragraph2}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
